import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Typography,
  Skeleton
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useHelperTextClasses } from '../BaseTextField';
import { useField } from 'formik';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import React, { Fragment, useCallback, useMemo } from 'react';
import { usePreferNotToSay } from './use-prefer-not-to-say.hook';

export type CheckboxesControlProps = {
  name: string;
  label?: string;
  className?: string;
  inline?: boolean;
  error?: boolean;
  helperText?: string;
  hideHelper?: boolean;
  disabled?: boolean;
  color?: 'primary' | 'secondary';
  options: (CheckboxGroupOption | string)[];
  loading?: boolean;
  includePreferNotToSay?: boolean;
};

export type CheckboxGroupOption = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  description?: React.ReactNode;
  className?: string;
};

export const CheckboxGroupRaw: React.VFC<Omit<CheckboxesControlProps, 'error' | 'name'> & {
  touched?: boolean;
  error?: string;
  value: unknown[];
  setValue: (value: unknown[], shouldValidate?: boolean) => void;
  setTouched?: (isTouched: boolean, shouldValidate?: boolean) => void;
}> = ({ color = 'secondary', ...props }) => {
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { classes, cx } = useCheckboxGroupStyles();
  const hasError = Boolean(props.error && props.helperText) || Boolean(props.error && props.touched);
  const helperText = (props.touched && props.error) || (!props.hideHelper && props.helperText);

  const optionsToMap = useMemo(() => props.options.map((opt) => {
    return typeof opt === 'string' ? { label: opt, value: opt } : opt;
  }), [ props.options ]);

  const onChange = useCallback((value: string, checked: boolean) => {
    const values = Array.isArray(props.value) ? props.value : [];
    const updatedValues = checked ? [ ...values, value ] : values.filter(v => !isEqual(v, value));
    const sorted = sortBy(updatedValues, (updatedValue) => {
      return optionsToMap.findIndex(option => isEqual(option.value, updatedValue));
    });

    props.setValue(sorted, true);
    props.setTouched?.(true, false);
  }, [ props.value, props.setValue, props.setTouched, optionsToMap ]);

  if (props.loading) {
    /*
        4 rows of skeletons is arbitrary
        42px height as average height of a checkbox
        250px width as average width of a checkbox

        if you need to, pass in props for estimated rows, height, and width
    */
    return (
      <Box>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton variant="text" height="42px" width="250px" key={index} animation="wave" />
        ))}
      </Box>
    );
  }

  return (
    <FormControl
      component="fieldset"
      error={hasError}
      disabled={props.disabled}
      color={color}
      className={props.className}
      fullWidth
    >
      <FormLabel focused={false}>{props.label}</FormLabel>
      <FormGroup row={props.inline}>
        {optionsToMap.map(({ label, value, disabled, description, className }, index) => {

          return (
            <Fragment key={index}>
              <FormControlLabel
                label={label}
                value={value}
                disabled={disabled}
                className={cx(classes.controlLabel, className)}
                checked={includes(props.value, value)}
                onChange={(_e, checked) => onChange(value, checked)}
                control={(
                  <Checkbox
                    color={color}
                    className={cx([ classes.radioRoot, {
                      [classes.error]: hasError
                    } ])}
                  />
                )}
              />
              {description && (
                <Box pl={3.75}>
                  {typeof description === 'string' ? <Typography color="textSecondary">{description}</Typography> : description}
                </Box>
              )}
            </Fragment>
          );
        })}
      </FormGroup>
      {helperText && (
        <FormHelperText className={helperTextClasses.root}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const CheckboxGroup: React.VFC<CheckboxesControlProps> = (props) => {
  const [ field, meta, helpers ] = useField<unknown[]>(props.name);
  const checkboxProps = usePreferNotToSay({
    name: props.name,
    options: props.options,
    includePreferNotToSay: props.includePreferNotToSay,
    value: field.value,
    setValue: helpers.setValue
  });

  return (
    <CheckboxGroupRaw
      {...props}
      {...checkboxProps}
      touched={meta.touched}
      error={meta.error}
      setTouched={helpers.setTouched}
    />
  );
};

const useCheckboxGroupStyles = makeStyles({ name: 'CheckboxGroup' })(theme => ({
  radioRoot: {
    '& svg:first-child': {
      transform: 'scale(1)',
    },
  },
  error: {
    color: theme.palette.error.main
  },
  controlLabel: {
    userSelect: 'none'
  }
}));
